import SEOMeta from "components/SEOMeta";
import { useBreadcrumb } from "components/breadcrumb/BreadcrumbContext";
import { brandConfig } from "config/brandConfig";
import { useEffect } from "react";

export default function PolitiqueConfidentialite() {
  const lastUpdated = "25 août 2025"; // mets à jour si besoin
  const { setItems } = useBreadcrumb();
  useEffect(() => {
    setItems([
      { label: "Accueil", href: "/" },
      { label: "Politique de confidentialité" },
    ]);
  }, [setItems]);
  return (
    <>
      <SEOMeta
        title="Politique de confidentialité"
        description={`Lisez la politique de confidentialité de ${brandConfig.brandName} pour comprendre comment nous protégeons vos données personnelles conformément à la LPD révisée et au RGPD.`}
      />
      <main
        role="main"
        style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}
      >
        <h1 style={{ marginTop: 12 }}>Politique de confidentialité</h1>
        <p style={{ fontSize: 14, opacity: 0.8, marginTop: 4 }}>
          Dernière mise à jour : {lastUpdated}
        </p>

        <section id="intro" style={{ marginTop: 24 }}>
          <p>
            La présente politique explique comment nous collectons, utilisons,
            partageons et protégeons vos données personnelles conformément à la{" "}
            <strong>LPD (Loi fédérale sur la protection des données)</strong>{" "}
            révisée (entrée en vigueur le 1er septembre 2023) et, le cas
            échéant, au RGPD pour les personnes concernées situées dans
            l’UE/EEE.
          </p>
        </section>

        <section id="identite" style={{ marginTop: 24 }}>
          <h2>1) Identité du responsable de traitement</h2>
          <p>
            <strong>Zamyk Sàrl</strong>
          </p>
          <address style={{ fontStyle: "normal" }}>
            Avenue des Boveresses 28, 1010 Lausanne, Suisse
          </address>
          <ul>
            <li>Forme juridique : Société à responsabilité limitée (Sàrl)</li>
            <li>IDE : CHE-154.918.158</li>
            <li>Registre du commerce : RC Vaud</li>
            <li>
              E-mail :{" "}
              <a href="mailto:contact@thetresorsafe.com">
                contact@thetresorsafe.com
              </a>
            </li>
            <li>
              Téléphone : <a href="tel:+41215190898">+41 21 519 08 98</a>
            </li>
          </ul>
          <p>
            Point de contact protection des données / DPO (le cas échéant) :{" "}
            <em>à préciser</em> (
            <a href="mailto:contact@thetresorsafe.com">
              contact@thetresorsafe.com
            </a>
            ).
          </p>
        </section>

        <section id="donnees-collectees" style={{ marginTop: 24 }}>
          <h2>2) Données que nous collectons</h2>
          <ul>
            <li>
              <strong>Données d’identification</strong> : nom, prénom, adresse
              postale, e‑mail, téléphone.
            </li>
            <li>
              <strong>Données de compte</strong> : identifiants, historique de
              commandes, préférences.
            </li>
            <li>
              <strong>Données de transaction</strong> : détails de commande,
              facturation, modes de paiement (les données de carte sont traitées
              par un prestataire de paiement certifié).
            </li>
            <li>
              <strong>Données techniques</strong> : adresse IP, identifiants
              d’appareil, type/versions de navigateur, pages consultées,
              horodatages, cookies/traceurs (voir section Cookies).
            </li>
            <li>
              <strong>Échanges</strong> : messages envoyés via formulaires,
              e‑mails, service client.
            </li>
            <li>
              <strong>Marketing</strong> : consentements, désabonnements,
              interactions avec nos campagnes.
            </li>
          </ul>
        </section>

        <section id="finalites-bases" style={{ marginTop: 24 }}>
          <h2>3) Finalités et bases légales</h2>
          <ul>
            <li>
              <strong>Fourniture du site et du service</strong> (création de
              compte, exécution du contrat, service client) — base : exécution
              contractuelle et intérêts légitimes.
            </li>
            <li>
              <strong>Traitement des commandes et paiements</strong> — base :
              exécution contractuelle / obligations légales.
            </li>
            <li>
              <strong>Sécurité et prévention des abus</strong> (journalisation,
              antifraude) — base : intérêts légitimes / obligations légales.
            </li>
            <li>
              <strong>Mesure d’audience et amélioration</strong> — base :
              consentement lorsque requis, sinon intérêts légitimes (mesure
              strictement nécessaire).
            </li>
            <li>
              <strong>Prospection/Newsletters</strong> — base : consentement
              (désabonnement possible à tout moment).
            </li>
            <li>
              <strong>Obligations légales</strong> (comptabilité, fiscalité,
              garanties) — base : obligations légales.
            </li>
          </ul>
        </section>

        <section id="cookies" style={{ marginTop: 24 }}>
          <h2>4) Cookies et technologies similaires</h2>
          <p>
            Nous utilisons des cookies/traceurs pour assurer le fonctionnement
            du site, mesurer l’audience et, si vous y consentez, personnaliser
            nos contenus et publicités. Lors de votre première visite, un
            bandeau de consentement vous permet d’accepter, refuser ou
            paramétrer vos choix.
          </p>
          <ul>
            <li>
              <strong>Cookies nécessaires</strong> (indispensables au
              fonctionnement, exemptés de consentement).
            </li>
            <li>
              <strong>Mesure d’audience</strong> (statistiques agrégées, durée
              de conservation limitée).
            </li>
            <li>
              <strong>Marketing</strong> (remarketing/publicité — déposés
              uniquement avec votre accord).
            </li>
          </ul>
          <p>
            Vous pouvez modifier vos préférences à tout moment via le bandeau de
            consentement ou les paramètres de votre navigateur.
          </p>
        </section>

        <section id="destinataires" style={{ marginTop: 24 }}>
          <h2>5) Destinataires et sous-traitants</h2>
          <p>
            L’accès à vos données est limité aux équipes habilitées de Zamyk
            Sàrl et à nos prestataires agissant pour notre compte (hébergement,
            maintenance, paiement, e‑mailing, analytics, logistique). Ces
            prestataires sont soumis à des obligations contractuelles de
            confidentialité et de sécurité.
          </p>
          <ul>
            <li>
              <strong>Hébergement & maintenance</strong> :{" "}
              <em>à préciser (p. ex. OVH, Infomaniak, etc.)</em>
            </li>
            <li>
              <strong>Paiement</strong> :{" "}
              <em>à préciser (p. ex. Stripe, PSP bancaire)</em>
            </li>
            <li>
              <strong>E‑mailing & support</strong> : <em>à préciser</em>
            </li>
            <li>
              <strong>Analytics</strong> :{" "}
              <em>à préciser (p. ex. Google Analytics)</em>
            </li>
            <li>
              <strong>Logistique & transport</strong> : <em>à préciser</em>
            </li>
          </ul>
          <p>
            Nous ne revendons pas vos données. En cas d’obligation légale, vos
            données peuvent être communiquées aux autorités compétentes.
          </p>
        </section>

        <section id="transferts" style={{ marginTop: 24 }}>
          <h2>6) Transferts internationaux</h2>
          <p>
            Si des données sont transférées hors de Suisse/EEE, nous veillons à
            l’existence d’un niveau de protection adéquat (décision
            d’adéquation, clauses contractuelles types et mesures
            supplémentaires si nécessaire).
          </p>
        </section>

        <section id="duree" style={{ marginTop: 24 }}>
          <h2>7) Durées de conservation (principes)</h2>
          <ul>
            <li>
              <strong>Compte & commandes</strong> : pendant la relation
              contractuelle, puis archivage légal (p. ex. obligations
              comptables/fiscales).
            </li>
            <li>
              <strong>Prospection</strong> : jusqu’au retrait de votre
              consentement ou 2 ans après le dernier contact actif.
            </li>
            <li>
              <strong>Logs techniques</strong> : quelques mois au plus, sauf
              nécessité de sécurité ou obligations légales.
            </li>
            <li>
              <strong>Cookies</strong> : selon leur finalité (voir paramétrage
              dans le bandeau de consentement).
            </li>
          </ul>
        </section>

        <section id="droits" style={{ marginTop: 24 }}>
          <h2>8) Vos droits</h2>
          <p>
            Selon la LPD (et le RGPD le cas échéant), vous disposez notamment
            des droits suivants :
          </p>
          <ul>
            <li>
              <strong>Droit d’accès et d’information</strong> sur les données
              vous concernant.
            </li>
            <li>
              <strong>Rectification</strong> des données inexactes ou
              incomplètes.
            </li>
            <li>
              <strong>Effacement</strong> lorsque les bases légales ne
              s’appliquent plus ou en cas d’opposition fondée.
            </li>
            <li>
              <strong>Portabilité</strong> des données que vous nous avez
              fournies et traitées de manière automatisée, lorsque applicable.
            </li>
            <li>
              <strong>Opposition</strong> au marketing direct à tout moment;
              limitation dans certains cas.
            </li>
            <li>
              <strong>Décision individuelle automatisée</strong> : droit de
              demander une intervention humaine et de contester la décision.
            </li>
          </ul>
          <p>
            Pour exercer vos droits :{" "}
            <a href="mailto:contact@thetresorsafe.com">
              contact@thetresorsafe.com
            </a>
            . Nous pouvons être amenés à vérifier votre identité. Vous avez
            également le droit de vous adresser au Préposé fédéral à la
            protection des données et à la transparence (PFPDT).
          </p>
        </section>

        <section id="securite" style={{ marginTop: 24 }}>
          <h2>9) Sécurité</h2>
          <p>
            Nous mettons en œuvre des mesures techniques et organisationnelles
            appropriées (contrôle d’accès, chiffrement en transit, sauvegardes,
            politiques internes, sélection de sous‑traitants conformes) pour
            protéger vos données contre l’accès non autorisé, la perte ou
            l’altération.
          </p>
        </section>

        <section id="mineurs" style={{ marginTop: 24 }}>
          <h2>10) Mineurs</h2>
          <p>
            Notre site ne s’adresse pas intentionnellement aux mineurs. Si vous
            pensez qu’un mineur nous a communiqué des données, contactez‑nous
            pour organiser la suppression des informations concernées.
          </p>
        </section>

        <section id="comms" style={{ marginTop: 24 }}>
          <h2>11) Communications marketing</h2>
          <p>
            Vous pouvez vous désabonner de nos e‑mails marketing via le lien de
            désinscription présent dans chaque message ou en nous écrivant à{" "}
            <a href="mailto:contact@thetresorsafe.com">
              contact@thetresorsafe.com
            </a>
            .
          </p>
        </section>

        <section id="modifs" style={{ marginTop: 24 }}>
          <h2>12) Modifications de cette politique</h2>
          <p>
            Nous pouvons mettre à jour cette politique pour refléter des
            évolutions légales, techniques ou opérationnelles. La version en
            vigueur est celle publiée sur cette page, avec la date indiquée en
            haut.
          </p>
        </section>
      </main>
    </>
  );
}
